// @file Surface attachments store
import { getDisplayAttributes } from '@@/bits/beethoven'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { Cid } from '@@/types'
import type { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { defineStore } from 'pinia'
import { ref } from 'vue'

export const useSurfaceAttachmentsStore = defineStore('surfaceAttachments', () => {
  const surfacePostsStore = useSurfacePostsStore()

  /* ---------------------- */
  /* STATE                  */
  /* ---------------------- */

  const fileUploadingToPickerBox = ref<File | null>(null)
  const linkDisplayAttributes = ref<Record<string, BeethovenDisplayAttributes | null>>({})
  const surfacePreviewImageLoaded = ref<Record<string, boolean>>({})

  /* ---------------------- */
  /* GETTERS                */
  /* ---------------------- */
  const getLinkDisplayAttributesForUrl = (url: string): BeethovenDisplayAttributes | null => {
    return linkDisplayAttributes.value[url] ?? null
  }

  const getLinkDisplayAttributesForPost = (postCid: string): BeethovenDisplayAttributes | null => {
    const post = surfacePostsStore.postEntitiesByCid[postCid]
    const attachmentUrl = post.wish_content?.attachment_props?.url ?? post.attachment
    return attachmentUrl != null ? getLinkDisplayAttributesForUrl(attachmentUrl) : null
  }

  const isSurfacePreviewImageLoadedForUrl = (url: string): boolean => {
    return surfacePreviewImageLoaded[url] ?? false
  }

  /* ---------------------- */
  /* ACTIONS                */
  /* ---------------------- */
  const uploadFileToPickerBox = (file: File | null): void => {
    fileUploadingToPickerBox.value = file
  }
  const completeUploadingFileToPickerBox = (): void => {
    fileUploadingToPickerBox.value = null
  }

  const updateLinkDisplayAttributes = ({
    url,
    attributes,
  }: {
    url: string
    attributes: BeethovenDisplayAttributes | null
  }): void => {
    linkDisplayAttributes.value = {
      ...linkDisplayAttributes.value,
      [url]: attributes,
    }
  }

  const cancelUploadFileToPickerBox = (): void => {
    fileUploadingToPickerBox.value = null
  }

  const setSurfacePreviewImageLoaded = ({ url }: { url?: string }): void => {
    if (url != null) {
      surfacePreviewImageLoaded.value = {
        ...surfacePreviewImageLoaded.value,
        [url]: true,
      }
    }
  }

  const fetchDisplayAttributesForPost = async (postCid: Cid): Promise<void> => {
    const post = surfacePostsStore.postEntitiesByCid[postCid]
    const attachmentUrl = post.wish_content?.attachment_props?.url ?? post.attachment
    if (attachmentUrl == null || linkDisplayAttributes.value[attachmentUrl] != null) return
    const displayAttributes = await getDisplayAttributes(attachmentUrl)
    updateLinkDisplayAttributes({ url: attachmentUrl, attributes: displayAttributes })
  }

  return {
    // state
    fileUploadingToPickerBox,
    linkDisplayAttributes,
    surfacePreviewImageLoaded,

    // actions
    getLinkDisplayAttributesForUrl,
    getLinkDisplayAttributesForPost,
    isSurfacePreviewImageLoadedForUrl,

    // actions
    uploadFileToPickerBox,
    cancelUploadFileToPickerBox,
    completeUploadingFileToPickerBox,
    updateLinkDisplayAttributes,
    setSurfacePreviewImageLoaded,
    fetchDisplayAttributesForPost,
  }
})
